import React, { FunctionComponent, useEffect, useRef } from 'react';
import { IMaskInput } from 'react-imask';
import { FieldInputProps, useFormikContext } from 'formik';
import { t } from 'i18next';
import { MaskedOptions } from 'imask';
import { countryToMobilePhoneDigitsMapping } from '../../resources/base/models';
import { FormField } from '../FormField/FormField';

export const masks = {
  zipCodeMask: '00000',
  sirenMask: '000000000',
  apeCodeMask: '0000a',
  licensePlateMask: 'aa-000-aa',
  phonePrefixContactMask: '+{33} 0000000000',
  ibanMask: 'aa00 **** **** **** **** **** **** ***',
  vinMask: /^[A-HJ-NPR-Za-hj-npr-z0-9]{0,17}$/,
  nameMask: /^[\p{L}' -]*$/u, // allows letters, spaces, apostrophes, and hyphens
  companyNameMask: /^[\p{L}\p{N}' -]*$/u, // allows letters (including accents), numbers, spaces, apostrophes, and hyphens
  cityEmployerMask: /^[\p{L}' -]*$/u, // allows letters (including accents), spaces, apostrophes, and hyphens
};

export const getMobilePhoneLengthMaskForCountry = (countryCode: string) => {
  const countryCodeKey = countryCode as keyof typeof countryToMobilePhoneDigitsMapping;
  const digits = countryToMobilePhoneDigitsMapping[countryCodeKey]?.digits || [];
  const maxLength = digits.length ? Math.max(...digits) : 15; // fallback

  return '0'.repeat(maxLength);
};

export type CharsReplacementMapType = Array<[string[], string]>;

export const charsReplacementMap1: CharsReplacementMapType = [[["'", '-'], ' ']];

interface Props {
  fieldName: string;
  labelTranslationKey?: string;
  mask: MaskedOptions<any>['mask'] | RegExp | string;
  optional?: boolean;
  onAccept?: (e: string) => void;
  placeholder?: string;
  centerText?: boolean;
  isDisabled?: boolean;
  infoTextTranslationKey?: string;
  overwrite?: boolean;
  /**
   * - Whether to transform special characters
   */
  onAcceptCharsReplacementMap?: CharsReplacementMapType;
  onBlurCapture?: (e: React.FocusEvent<HTMLInputElement>) => void;
  /**
   * - Whether to remove whitespace from actual form value if if mask
   * includes one.
   */
  removeWhitespace?: boolean;
  allowAccents?: boolean;
}

/**
 * Note: All input values are implicitly converted to uppercase.
 */
export const MaskedTextFormField: FunctionComponent<Props> = ({
  labelTranslationKey = '',
  fieldName,
  mask,
  onAccept,
  placeholder,
  centerText,
  optional = false,
  isDisabled,
  infoTextTranslationKey,
  overwrite = false,
  onBlurCapture,
  removeWhitespace,
  onAcceptCharsReplacementMap,
  allowAccents = false,
}) => {
  const { setFieldTouched, setFieldValue, values } = useFormikContext<any>();
  const inputRef = useRef<HTMLInputElement>(null);
  const value = values[fieldName];

  useEffect(() => {
    if (values[fieldName] === undefined && inputRef.current) {
      inputRef.current.value = '';
    } else if (inputRef.current && values[fieldName] !== inputRef.current.value) {
      inputRef.current.value = value;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    setFieldTouched(fieldName, true);

    const { value } = e.target;
    setFieldValue(fieldName, removeWhitespace ? value.replaceAll(' ', '') : value);

    onBlurCapture?.(e);
  };

  const handleAccept = (value: string) => {
    let transformedValue = value.replace(/\s+/g, ' ').toUpperCase();

    if (!allowAccents) {
      transformedValue = transformedValue.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    }
    // Apply custom character replacements if map is provided
    if (onAcceptCharsReplacementMap) {
      onAcceptCharsReplacementMap.forEach(([charsToReplace, charToReplaceWith]) => {
        charsToReplace.forEach((char) => {
          transformedValue = transformedValue.replace(char, charToReplaceWith);
        });
      });
    }

    setFieldValue(fieldName, removeWhitespace ? transformedValue.replaceAll(' ', '') : transformedValue);
    if (onAccept) onAccept(transformedValue);
  };

  return (
    <FormField
      name={fieldName}
      type="input"
      labelText={`${t(labelTranslationKey)}`}
      required={!optional}
      infoText={infoTextTranslationKey ? t(infoTextTranslationKey) : ''}
      render={(fieldProps: FieldInputProps<any>) => (
        <IMaskInput
          {...fieldProps}
          name={fieldName}
          inputRef={inputRef}
          className={centerText ? 'c-input__input u-text-center' : 'c-input__input'}
          mask={mask}
          disabled={isDisabled}
          placeholder={placeholder}
          onBlurCapture={handleBlur}
          onAccept={handleAccept}
          overwrite={overwrite}
        />
      )}
    />
  );
};
